import { useRhAuth } from '../../../contexts/RhAuthContext';
import dayjs from 'dayjs';
import _ from 'lodash';
import Tooltip from 'rc-tooltip';
import React, { useMemo } from 'react';
import { BiCheck } from 'react-icons/bi';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import { ArrowSmallLeftSvg } from '../../../assets/svg-icon-components/ArrowSmallLeftSvg';
import { TimeCheckSvg } from '../../../assets/svg-icon-components/TimeCheckSvg';
import { getIntroVideoUrl } from '../../../util/intro-video';
import { useUserRoles } from '../../../util/use-user-role';
import { DashboardHeader } from '../../core/DashboardHeader';
import { DashboardWrapper } from '../../core/DashboardWrapper';
import { HorizontalContent } from '../../core/HorizontalContent';
import { Can } from '../../core/rbac/Can';
import { RhAuthorInfo } from '../../core/RhAuthorInfo';
import { RhButton, RhButtonMode } from '../../core/RhButton';
import { RhCard } from '../../core/RhCard';
import { RhFormGrid, RhFormGridColumn } from '../../core/RhFormGrid';
import { RhIconText } from '../../core/RhIconText';
import { RhImage } from '../../core/RhImage';
import { RhMoreDropdown } from '../../core/RhMoreDropdown';
import { RhRawButton } from '../../core/RhRawButton';
import { RhTag } from '../../core/RhTag';
import { SizedBox } from '../../core/SizedBox';
import { colors, Description, H1, H2, H4, Regular } from '../../core/Styles';
import { getModuleGroupKey, getModuleGroupsLibrary, getModuleTagsBySection } from '../bible-study-form.helper';
import { ViewBibleStudyProps } from '../containers/ViewBibleStudyContainer';
import { GuidedContentCard } from './shared/GuidedContentCard';
import { StudySectionCard } from './shared/StudySectionCard';
import { StudySectionCardsDiv } from './shared/StudySectionCards';
import { StudySectionDayCard } from './shared/StudySectionDayCard';
import { StudySectionModuleCard } from './shared/StudySectionModuleCard';
import { StudySectionModuleCardsDiv } from './shared/StudySectionModuleCards';
import { ViewModuleModal } from './shared/ViewModuleModal';

export const ViewBibleStudyScreen = (props: ViewBibleStudyProps) => {
  const { roles } = useUserRoles();
  const { user } = useRhAuth();

  const canEditStudy = roles?.[0] !== 'Content Creator' || props.bibleStudy?.author === user?.sub;

  const moduleLibrary = useMemo(() => getModuleGroupsLibrary(props.bibleStudy?.sections || []), [
    props.bibleStudy?.sections,
  ]);

  return (
    <DashboardWrapper>
      <DashboardHeader
        title={
          <RhRawButton onClick={props.userClickedBackButton}>
            <Title>
              <ArrowSmallLeftSvg fontSize={30} fill={colors.normal} />
              <H1>Bible Studies</H1>
            </Title>
          </RhRawButton>
        }
        actions={[
          <Can
            key="mark-featured"
            role={roles?.[0]}
            perform="update:programs__mark_featured"
            yes={
              <Tooltip
                placement="left"
                overlay={
                  <Regular color={colors.background} weight="600">
                    {props.bibleStudy?.isFeatured
                      ? 'Click to remove from Featured Studies'
                      : 'Click to add to Featured Studies'}
                  </Regular>
                }>
                {props.bibleStudy?.isFeatured ? (
                  <RhButton
                    mode={RhButtonMode.secondary}
                    disabled={props.isLoadingStudy}
                    loading={props.isLoadingStudy}
                    title={
                      <div className="flex items-center space-x-1">
                        <BiCheck size={24} color={colors.primary} />
                        <H2 color={colors.primary}>Featured</H2>
                      </div>
                    }
                    onClick={e => {
                      props.userToggledFeatureStatus({ isFeatured: false });
                    }}
                  />
                ) : (
                  <RhButton
                    mode={RhButtonMode.secondary}
                    title="Add to Featured"
                    disabled={props.isLoadingStudy}
                    loading={props.isLoadingStudy}
                    onClick={e => {
                      props.userToggledFeatureStatus({ isFeatured: true });
                    }}
                  />
                )}
              </Tooltip>
            }
            no={
              props.bibleStudy?.isFeatured && (
                <RhButton
                  mode={RhButtonMode.secondary}
                  style={{ padding: '6px 12px' }}
                  title={
                    <div className="flex items-center space-x-1">
                      <BiCheck size={24} color={colors.primary} />
                      <H2 color={colors.primary}>Featured</H2>
                    </div>
                  }
                />
              )
            }
          />,
          <Can
            key="edit-study"
            role={roles?.[0]}
            perform="update:programs"
            yes={
              <RhButton
                title="Edit Study"
                loading={props.isLoadingStudy}
                disabled={props.isLoadingStudy || !canEditStudy}
                onClick={props.userClickedEditStudy}
              />
            }
          />,
          <RhMoreDropdown
            key="more-dropdown"
            deletePermission="delete:programs"
            userClickedDelete={props.userClickedDeleteStudy}
          />,
        ]}
      />
      <RhFormGrid>
        <RhFormGridColumn>
          {/* title and description */}
          <RhCard>
            <H1>{props.bibleStudy?.title}</H1>
            <SizedBox height={16} />
            <Description>{props.bibleStudy?.description}</Description>
          </RhCard>
          {/* bible study sections */}
          <RhCard mode="tiled">
            <HorizontalContent left={<H2>Sections</H2>} />
            {!!props.bibleStudy?.sections.length && (
              <StudySectionCardsDiv>
                {props.bibleStudy.sections.map((section, sectionIndex) => {
                  const tags = getModuleTagsBySection(section);
                  return (
                    <StudySectionCard key={`${section.name}${sectionIndex}`} title={section.name} isEditMode={false}>
                      {tags.map(tag => {
                        const key = getModuleGroupKey(section.tag || section.name, tag);
                        const modules = moduleLibrary[key];
                        if (!modules?.length) return null;
                        return (
                          <StudySectionDayCard key={tag} title={tag}>
                            <StudySectionModuleCardsDiv>
                              {modules.map(module => (
                                <StudySectionModuleCard
                                  key={key}
                                  module={module}
                                  onClick={() => props.userClickedOnModule(module)}
                                />
                              ))}
                            </StudySectionModuleCardsDiv>
                          </StudySectionDayCard>
                        );
                      })}
                    </StudySectionCard>
                  );
                })}
              </StudySectionCardsDiv>
            )}
          </RhCard>
        </RhFormGridColumn>
        <RhFormGridColumn>
          <RhCard mode="tiled">
            <H2>Publishing Status</H2>
            <SizedBox height={12} />
            <RhIconText icon={TimeCheckSvg} text={_.startCase(props.bibleStudy?.publishingStatus) || '-'} />
          </RhCard>
          <RhCard mode="tiled">
            <H2>Categories</H2>
            {!!props.bibleStudy?.categories.length && (
              <Tags>
                {props.bibleStudy.categories.map(category => (
                  <RhTag key={category} editable={false} type="block" title={category} className="p-1" />
                ))}
              </Tags>
            )}
          </RhCard>
          {/* <RhCard mode="tiled">
            <H2>Time to Complete</H2>
            {!!props.bibleStudy?.duration && (
              <>
                <SizedBox height={12} />
                <RhIconText text={formatTime(props.bibleStudy?.duration || 0)} />
              </>
            )}
          </RhCard> */}
          <RhCard mode="tiled">
            <H2>Created By:</H2>
            <SizedBox height={12} />
            <RhAuthorInfo
              authorLabel=""
              author={_.startCase(props.bibleStudy?.authorName) || '-'}
              subtitleDateLabel=""
              subtitleDate={props.bibleStudy?.dateCreated || ''}
              authorImage={props.bibleStudy?.authorImage}
            />
            <SizedBox height={14} />
            <H4>LAST MODIFIED BY {_.startCase(props.bibleStudy?.updatedByName) || '-'}</H4>
            <H4 up>{dayjs(props.bibleStudy?.dateUpdated).format('ddd, MMM DD, YYYY | hh:mmA')}</H4>
          </RhCard>
          <RhCard mode="tiled">
            <H2>Featured Image</H2>
            {!!props.bibleStudy?.imageUrl && (
              <>
                <SizedBox height={12} />
                <RhImage mode="full" src={props.bibleStudy.imageUrl} />
              </>
            )}
          </RhCard>
          <RhCard mode="tiled">
            <H2>Introduction Video</H2>
            {!!props.bibleStudy?.introMuxId && (
              <PlayerContainer>
                <ReactPlayer
                  url={getIntroVideoUrl(props.bibleStudy.introMuxId)}
                  controls={true}
                  width="100%"
                  height="100%"
                  style={styles.player}
                />
              </PlayerContainer>
            )}
          </RhCard>
          <RhCard mode="tiled">
            <H2>Tags</H2>
            {!!props.bibleStudy?.tags.length && (
              <Tags>
                {props.bibleStudy.tags.map(tag => (
                  <RhTag key={tag} editable={false} title={tag} className="p-1" />
                ))}
              </Tags>
            )}
          </RhCard>
          {/* TODO: add taxonomic category if API is updated */}
          {!!props.bibleStudy?.contentMaps && <GuidedContentCard contentMaps={props.bibleStudy?.contentMaps} />}
        </RhFormGridColumn>
      </RhFormGrid>

      {!!props.moduleInView && (
        <ViewModuleModal
          isOpen={!!props.moduleInView}
          onRequestClose={props.userClickedOutModule}
          module={props.moduleInView}
        />
      )}
    </DashboardWrapper>
  );
};

const styles = {
  player: {
    borderRadius: 16,
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    left: 0,
  } as React.CSSProperties,
};

const Title = styled.div`
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 8px;
  }
`;

const Tags = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  & > * {
    margin-right: 8px;
    margin-bottom: 8px;
  }
`;

const PlayerContainer = styled.div`
  width: 100%;
  margin-top: 12px;
  position: relative;
  // fixed aspect ratio box
  // see: https://github.com/cookpete/react-player#responsive-player
  padding-top: 56.25%;
`;
